import { useContext } from 'react'

import { launchModal } from '@domain-group/javascript-utils'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { baseURL } from '@functions/api'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import * as campaignQuoteTemplateActions from '@redux/modules/campaignQuoteTemplate'

import defaultFormState, { requiredFields } from '@models/campaignQuoteTemplate'

import PageContext from '@contexts/pageContext'

import type { AppDispatch } from '@redux/store'
import type { DeleteParams, ModuleState } from '@redux/modules/types'
import type {
  CampaignQuoteTemplateModel,
  CampaignQuoteTemplateRequestOptions,
} from '@models/types'

export type GenerateUrlsOptions = {
  prefixRoute?: string,
  payload?: Record<string, string>,
}

export const generateUrls = (
  campaignQuoteTemplate: Partial<CampaignQuoteTemplateModel>,
  options?: GenerateUrlsOptions,
) => {
  const { id } = campaignQuoteTemplate || {}
  const { prefixRoute = 'systemManager', payload } = options || {}

  const params = new URLSearchParams(payload).toString()

  return {
    archivedCampaignQuoteTemplatesUrl: `#/${prefixRoute}/campaignQuoteTemplates?hidden=true`,
    campaignQuoteTemplatesIndexUrl: `#/${prefixRoute}/campaignQuoteTemplates?hidden=false`,
    editCampaignQuoteTemplateSettingsUrl: `#/${prefixRoute}/campaignQuoteTemplates/${id}/edit/settings`,
    editCampaignQuoteTemplateUrl: `#/${prefixRoute}/campaignQuoteTemplates/${id}/edit`,
    pdfDownloadUrl: `${baseURL}/campaign_quote_templates/${id}/pdf.json?${params}`,
  }
}

type CreateCampaignQuoteTemplateParams = {
  campaignQuoteTemplateParams: {
    campaignQuoteId: number,
    campaignQuoteTemplate: Partial<CampaignQuoteTemplateModel>,
  },
  dispatch: AppDispatch,
  requestOptions?: CampaignQuoteTemplateRequestOptions,
}

const createCampaignQuoteTemplate = (params: CreateCampaignQuoteTemplateParams) => {
  const { dispatch, campaignQuoteTemplateParams, requestOptions } = params
  const { createCampaignQuoteTemplate: createFn } = campaignQuoteTemplateActions

  // @ts-expect-error TS2559
  return dispatch(createFn(campaignQuoteTemplateParams, requestOptions)).then((response) => {
    const { success } = response

    if (success){
      const { data } = response
      const { agency_id, id } = data

      const { editCampaignQuoteTemplateUrl } = generateUrls({ id }, { prefixRoute: `agencies/${agency_id}/manage` })

      return {
        success,
        data,
        redirectUrl: editCampaignQuoteTemplateUrl,
      }
    }

    return response
  })
}

type CreateCampaignQuoteTemplateFromQuoteParams = {
  campaignQuoteId: number,
  campaignQuoteTemplateParams: Partial<CampaignQuoteTemplateModel>,
  dispatch: AppDispatch,
  requestOptions?: CampaignQuoteTemplateRequestOptions,
}

const createCampaignQuoteTemplateFromQuote = (params: CreateCampaignQuoteTemplateFromQuoteParams) => {
  const {
    dispatch, campaignQuoteId, campaignQuoteTemplateParams, requestOptions,
  } = params
  const { createCampaignQuoteTemplateFromQuote: createFn } = campaignQuoteTemplateActions

  // @ts-expect-error TS2554
  return dispatch(createFn(campaignQuoteId, campaignQuoteTemplateParams, requestOptions))
}

type UpdateCampaignQuoteTemplateParams = {
  campaignQuoteTemplate: CampaignQuoteTemplateModel,
  campaignQuoteTemplateParams: Partial<CampaignQuoteTemplateModel>,
  dispatch: AppDispatch,
  requestOptions?: CampaignQuoteTemplateRequestOptions,
}

const updateCampaignQuoteTemplate = (params: UpdateCampaignQuoteTemplateParams) => {
  const {
    dispatch, campaignQuoteTemplate, campaignQuoteTemplateParams, requestOptions,
  } = params
  const { updateCampaignQuoteTemplate: updateFn } = campaignQuoteTemplateActions

  const updatedParams = {
    id: campaignQuoteTemplate.id,
    ...campaignQuoteTemplateParams,
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

type SortedSectionItemIds = {
  [key: string]: number[],
}

type UpdateQuoteTemplateSectionItemOrderParams = {
  campaignQuoteTemplate: CampaignQuoteTemplateModel,
  dispatch: AppDispatch,
  requestOptions?: CampaignQuoteTemplateRequestOptions,
  sortedSectionItemIds: SortedSectionItemIds,
}

const updateCampaignQuoteTemplateSectionItemOrder = (params: UpdateQuoteTemplateSectionItemOrderParams) => {
  const {
    campaignQuoteTemplate, dispatch, requestOptions, sortedSectionItemIds,
  } = params
  const { updateCampaignQuoteTemplateSectionItemOrder: updateFn } = campaignQuoteTemplateActions

  return dispatch(updateFn(campaignQuoteTemplate, sortedSectionItemIds, requestOptions))
}

type DuplicateCampaignQuoteTemplateParams = {
  campaignQuoteTemplate: CampaignQuoteTemplateModel,
  dispatch: AppDispatch,
  generateUrlsOptions?: GenerateUrlsOptions,
  requestOptions?: CampaignQuoteTemplateRequestOptions,
}

const duplicateCampaignQuoteTemplate = (params: DuplicateCampaignQuoteTemplateParams) => {
  const {
    dispatch, campaignQuoteTemplate, generateUrlsOptions, requestOptions,
  } = params
  const { duplicateCampaignQuoteTemplate: duplicateFn } = campaignQuoteTemplateActions

  return dispatch(duplicateFn(campaignQuoteTemplate, requestOptions)).then((response) => {
    const { success } = response

    if (success){
      const { data } = response

      const { editCampaignQuoteTemplateUrl } = generateUrls(data, generateUrlsOptions)

      return {
        success,
        data,
        redirectUrl: editCampaignQuoteTemplateUrl,
      }
    }

    return response
  })
}

type RecalculateQuoteTemplatePricesParams = {
  campaignQuoteTemplate: CampaignQuoteTemplateModel,
  dispatch: AppDispatch,
  requestOptions?: CampaignQuoteTemplateRequestOptions,
}

const recalculateQuoteTemplatePrices = (params: RecalculateQuoteTemplatePricesParams) => {
  const {
    dispatch, campaignQuoteTemplate, requestOptions,
  } = params
  const { recalculateCampaignQuoteTemplatePrices: recalculateFn } = campaignQuoteTemplateActions

  return dispatch(recalculateFn(campaignQuoteTemplate, requestOptions))
}

type ToggleHideCampaignQuoteTemplateParams = {
  campaignQuoteTemplate: CampaignQuoteTemplateModel,
  dispatch: AppDispatch,
  requestOptions?: CampaignQuoteTemplateRequestOptions,
}

const toggleHideCampaignQuoteTemplate = (params: ToggleHideCampaignQuoteTemplateParams) => {
  const { campaignQuoteTemplate, dispatch, requestOptions } = params

  const { toggleHideCampaignQuoteTemplate: toggleHiddenFn } = campaignQuoteTemplateActions

  return dispatch(toggleHiddenFn(campaignQuoteTemplate, requestOptions))
}

type DeleteCampaignQuoteTemplateParams = {
  campaignQuoteTemplate: DeleteParams<CampaignQuoteTemplateModel>,
  dispatch: AppDispatch,
  generateUrlsOptions?: GenerateUrlsOptions,
}

const deleteCampaignQuoteTemplate = (params: DeleteCampaignQuoteTemplateParams) => {
  const { dispatch, campaignQuoteTemplate, generateUrlsOptions } = params
  const { deleteCampaignQuoteTemplate: deleteFn } = campaignQuoteTemplateActions

  return dispatch(deleteFn(campaignQuoteTemplate)).then((response) => {
    const { success } = response

    if (success){
      const { data } = response

      const { campaignQuoteTemplatesIndexUrl } = generateUrls(data, generateUrlsOptions)

      return {
        success,
        data,
        redirectUrl: campaignQuoteTemplatesIndexUrl,
      }
    }

    return response
  })
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useCampaignQuoteTemplateForm(
  campaignQuoteTemplate: Partial<CampaignQuoteTemplateModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const campaignQuoteTemplateForm = useForm(
    defaultFormState,
    { entity: campaignQuoteTemplate, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [campaignQuoteTemplate.id, campaignQuoteTemplate.cache_key],
  )

  return {
    ...campaignQuoteTemplateForm,
  }
}

export const useRelations = (campaignQuoteTemplate: Partial<CampaignQuoteTemplateModel> = {}) => {
  const { agency_id } = campaignQuoteTemplate

  const { agencies } = useSelector(reduxState => reduxState.entities)

  const agency = agency_id && agencies[agency_id]
    ? agencies[agency_id]
    : {}

  return {
    agency,
  }
}

type Options = {
  generateUrlsOptions?: GenerateUrlsOptions,
  performHttpRequests?: boolean,
  requestOptions?: CampaignQuoteTemplateRequestOptions,
}

function useCampaignQuoteTemplate(initEntity: Partial<CampaignQuoteTemplateModel> = {}, options: Options = {}) {
  const {
    generateUrlsOptions,
    performHttpRequests,
    requestOptions,
  } = options

  // @ts-expect-error TS2322
  const { entity: campaignQuoteTemplate }: { entity: CampaignQuoteTemplateModel } = useLatestEntity(
    initEntity,
    'campaignQuoteTemplates',
  )
  const { id } = campaignQuoteTemplate

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const { agency } = useRelations(campaignQuoteTemplate)

  useReduxAction(
    'campaignQuoteTemplates',
    'loadCampaignQuoteTemplate',
    {
      entityId: id,
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: ({ loading }: ModuleState) => !!performHttpRequests && !loading,
    },
  )

  const {
    creating, deleting, loadedIds, loading, updating,
  } = useSelector(reduxState => reduxState.campaignQuoteTemplates)

  return {
    agency,
    callbacks: {
      createCampaignQuoteTemplate: (
        campaignQuoteTemplateParams: Partial<CampaignQuoteTemplateModel>,
        entityOptions?: CampaignQuoteTemplateRequestOptions,
      ) => (
        // @ts-expect-error TS2739
        createCampaignQuoteTemplate({ campaignQuoteTemplateParams, dispatch, requestOptions: entityOptions })
      ),
      createCampaignQuoteTemplateFromQuote: (
        campaignQuoteId: number,
        campaignQuoteTemplateParams: Partial<CampaignQuoteTemplateModel>,
        entityOptions?: CampaignQuoteTemplateRequestOptions,
      ) => (
        createCampaignQuoteTemplateFromQuote({
          campaignQuoteId, campaignQuoteTemplateParams, dispatch, requestOptions: entityOptions,
        })
      ),
      createOrEditCampaignQuoteTemplate: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditCampaignQuoteTemplateModal',
        payload: { campaignQuoteTemplate },
      }),
      deleteCampaignQuoteTemplate: () => deleteCampaignQuoteTemplate({
        dispatch,
        campaignQuoteTemplate,
        generateUrlsOptions,
      }),
      downloadQuoteTemplate: () => launchModal({
        callbacks,
        modalKey: 'DownloadQuoteTemplateModal',
        payload: { campaignQuoteTemplate },
      }),
      duplicateCampaignQuoteTemplate: (
        entityOptions?: CampaignQuoteTemplateRequestOptions,
      ) => (
        duplicateCampaignQuoteTemplate({
          campaignQuoteTemplate,
          dispatch,
          generateUrlsOptions,
          requestOptions: entityOptions,
        })
      ),
      editSectionSort: () => launchModal({
        callbacks,
        modalKey: 'EditCampaignQuoteTemplateSectionSortModal',
        payload: { campaignQuoteTemplate },
      }),
      recalculateQuoteTemplatePrices: (entityOptions?: CampaignQuoteTemplateRequestOptions) => (
        recalculateQuoteTemplatePrices({
          campaignQuoteTemplate, dispatch, requestOptions: entityOptions,
        })
      ),
      saveQuoteAsTemplate: (campaignQuoteId: number) => launchModal({
        callbacks,
        modalKey: 'SaveQuoteAsTemplateModal',
        payload: { campaignQuoteId, campaignQuoteTemplate },
      }),
      toggleHideCampaignQuoteTemplate: (
        entityOptions?: CampaignQuoteTemplateRequestOptions,
      ) => toggleHideCampaignQuoteTemplate({
        campaignQuoteTemplate, dispatch, requestOptions: entityOptions,
      }),
      updateCampaignQuoteTemplate: (
        campaignQuoteTemplateParams: Partial<CampaignQuoteTemplateModel>,
        entityOptions?: CampaignQuoteTemplateRequestOptions,
      ) => (
        updateCampaignQuoteTemplate({
          campaignQuoteTemplate, campaignQuoteTemplateParams, dispatch, requestOptions: entityOptions,
        })
      ),
      updateCampaignQuoteTemplateSectionItemOrder: (
        sortedSectionItemIds: SortedSectionItemIds,
        entityOptions?: CampaignQuoteTemplateRequestOptions,
      ) => updateCampaignQuoteTemplateSectionItemOrder({
        campaignQuoteTemplate,
        dispatch,
        requestOptions: entityOptions,
        sortedSectionItemIds,
      }),
    },
    campaignQuoteTemplate,
    creating,
    deleting,
    loading: loading && !loadedIds.includes(campaignQuoteTemplate.id),
    updating,
    urls: generateUrls(campaignQuoteTemplate, generateUrlsOptions),
  }
}

export type UseCampaignQuoteTemplatePayload = ReturnType<typeof useCampaignQuoteTemplate>

export default useCampaignQuoteTemplate
